/**
 * AccountSlice Selectors
 * 
 * Simple selectors for accessing account slice state.
 * UI components should drill into the account object for specific fields.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { AccountSliceState } from './state';

// Base selector to get the account slice
const selectAccountSlice = (state: RootState): AccountSliceState => state.account;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current account entity.
 * UI components should drill into this object for specific fields.
 */
export const selectCurrentAccount = createSelector(
  selectAccountSlice,
  (slice) => slice.currentAccount
);

/**
 * Select the account backup (for discard functionality)
 */
export const selectAccountBackup = createSelector(
  selectAccountSlice,
  (slice) => slice.accountBackup
);

/**
 * Select loading state
 */
export const selectIsLoading = createSelector(
  selectAccountSlice,
  (slice) => slice.isLoading
);

// =============================================================================
// EDITING STATE SELECTORS
// =============================================================================

/**
 * Select whether there are unsaved changes
 */
export const selectIsEdited = createSelector(
  selectAccountSlice,
  (slice) => slice.isEdited
);

/**
 * Select the list of edited field names
 */
export const selectEditedFields = createSelector(
  selectAccountSlice,
  (slice) => slice.editedFields
);

// =============================================================================
// VALIDATION SELECTORS
// =============================================================================

/**
 * Select all validation errors
 */
export const selectValidationErrors = createSelector(
  selectAccountSlice,
  (slice) => slice.validationErrors
);

/**
 * Select whether there are any validation errors
 */
export const selectHasValidationErrors = createSelector(
  selectValidationErrors,
  (errors) => Object.keys(errors).length > 0
);
